import { ReactNode } from "react";
import { Filter } from "lucide-react";
import { classNames } from "../../UI/Utils/classNames.ts";
import { SALES_GATEWAYS, SalesGateway } from "./SaleRecord.ts";
import { SALES_GATEWAY_LABELS } from "./salesLabels.ts";
import {
  activeSalesFilterCount,
  NO_SALES_FILTERS,
  SalesFilters,
} from "./salesFilters.ts";
import {
  SalesComparison,
  SalesPeriod,
  SalesPeriodPreset,
} from "./salesPeriod.ts";
import { SoldItemFilterField } from "./SoldItemFilterField.tsx";
import { FilterOption, OptionsFilterField } from "./OptionsFilterField.tsx";
import { PeriodChoice, PeriodFilterField } from "./PeriodFilterField.tsx";

export type SalesFilterOption = FilterOption;

const GATEWAY_OPTIONS = SALES_GATEWAYS.map((gateway) => ({
  id: gateway,
  label: SALES_GATEWAY_LABELS[gateway],
}));

function FilterField({
  label,
  isActive = false,
  className = "w-full sm:w-56",
  children,
}: {
  label: string;
  isActive?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-1 text-xs text-gray-500 ${className}`}>
      <span
        className={classNames(
          "flex items-center gap-1",
          isActive ? "font-medium text-blue-700" : undefined,
        )}
      >
        {label}
        {isActive && (
          <Filter className="w-3 h-3 fill-current" aria-label="Filtro attivo" />
        )}
      </span>
      <div className={isActive ? "rounded-lg ring-2 ring-blue-600" : undefined}>
        {children}
      </div>
    </div>
  );
}

export function SalesFiltersBar({
  preset,
  period,
  comparison,
  filters,
  paymentMethodOptions,
  installmentsOptions,
  itemLabelOf,
  onChangePeriod,
  onChangeFilters,
}: {
  preset: SalesPeriodPreset;
  period: SalesPeriod;
  comparison: SalesComparison;
  filters: SalesFilters;
  paymentMethodOptions: SalesFilterOption[];
  installmentsOptions: SalesFilterOption[];
  itemLabelOf: (itemKey: string) => string;
  onChangePeriod: (choice: PeriodChoice) => void;
  onChangeFilters: (filters: SalesFilters) => void;
}) {
  const changeFilter = (change: Partial<SalesFilters>) =>
    onChangeFilters({ ...filters, ...change });
  const activeFilterCount = activeSalesFilterCount(filters);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <FilterField label="Periodo" className="w-full sm:w-64">
        <PeriodFilterField
          preset={preset}
          period={period}
          comparison={comparison}
          onChange={onChangePeriod}
        />
      </FilterField>
      <FilterField label="Gateway" isActive={filters.gateways.length > 0}>
        <OptionsFilterField
          options={GATEWAY_OPTIONS}
          value={filters.gateways}
          nothingSelected="Tutti"
          clearLabel="Rimuovi il filtro gateway"
          onChange={(gateways) =>
            changeFilter({ gateways: gateways as SalesGateway[] })
          }
        />
      </FilterField>
      <FilterField
        label="Metodo di pagamento"
        isActive={filters.paymentMethods.length > 0}
      >
        <OptionsFilterField
          options={paymentMethodOptions}
          value={filters.paymentMethods}
          nothingSelected="Tutti"
          clearLabel="Rimuovi il filtro metodo di pagamento"
          onChange={(paymentMethods) => changeFilter({ paymentMethods })}
        />
      </FilterField>
      <FilterField
        label="Rate scelte"
        isActive={filters.installments.length > 0}
      >
        <OptionsFilterField
          options={installmentsOptions}
          value={filters.installments}
          nothingSelected="Tutte"
          clearLabel="Rimuovi il filtro rate"
          onChange={(installments) => changeFilter({ installments })}
        />
      </FilterField>
      <FilterField
        label="Prodotto o bundle"
        isActive={filters.items.length > 0}
        className="w-full sm:w-72"
      >
        <SoldItemFilterField
          value={filters.items}
          itemLabelOf={itemLabelOf}
          onChange={(items) => changeFilter({ items })}
        />
      </FilterField>
      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={() => onChangeFilters(NO_SALES_FILTERS)}
          className="h-10 text-sm text-blue-600 hover:underline"
        >
          Rimuovi{" "}
          {activeFilterCount === 1
            ? "il filtro"
            : `i ${activeFilterCount} filtri`}
        </button>
      )}
    </div>
  );
}
